import React from "react";

type BartenderPickIconProps = {
  className?: string;
};

/**
 * Icon for the "AI Picks for Me" menu option: the RUSH AI bartender's face —
 * his glowing green AI eyes signal it's the AI — presenting a freshly-picked
 * cocktail with a sparkle, so the guest reads it as "the AI bartender chooses
 * a drink for me". Flat fills, square 100×100 viewBox so it scales cleanly.
 */
export default function BartenderPickIcon({ className = "" }: BartenderPickIconProps) {
  return (
    <svg
      viewBox="0 0 100 100"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-hidden
    >
      {/* Shoulders / vest */}
      <path d="M26 96 C 26 81 36 75 49 75 C 62 75 72 81 72 96 Z" fill="#28323f" />
      {/* Neck */}
      <rect x="44.5" y="61" width="9" height="17" rx="3" fill="#e7b184" />
      {/* Red bow tie */}
      <g transform="translate(49 75)">
        <path d="M0 0 L -8.5 -3.8 Q -11 0 -8.5 3.8 Z" fill="#e23a4e" stroke="#9e0f22" strokeWidth="0.7" />
        <path d="M0 0 L 8.5 -3.8 Q 11 0 8.5 3.8 Z" fill="#e23a4e" stroke="#9e0f22" strokeWidth="0.7" />
        <rect x="-2" y="-2.4" width="4" height="4.8" rx="1.1" fill="#b51228" />
      </g>

      {/* Ears */}
      <ellipse cx="30" cy="47" rx="3" ry="5.2" fill="#e7b184" />
      <ellipse cx="68" cy="47" rx="3" ry="5.2" fill="#e7b184" />
      {/* Head */}
      <ellipse cx="49" cy="45" rx="19" ry="22" fill="#f6c79e" />
      {/* Hair */}
      <path d="M30 47 C 27 24 39 15 49 15 C 59 15 71 24 68 47 C 65 32 57 27 49 27 C 41 27 33 32 30 47 Z" fill="#34281d" />
      {/* Brows */}
      <path d="M37 42 Q42 38.5 46 42" stroke="#2b2118" strokeWidth="1.7" strokeLinecap="round" fill="none" />
      <path d="M52 42 Q57 38.5 61 42" stroke="#2b2118" strokeWidth="1.7" strokeLinecap="round" fill="none" />
      {/* Eyes — AI green iris, with a blink */}
      <g>
        <ellipse cx="41" cy="48" rx="4.2" ry="3.2" fill="#ffffff" />
        <ellipse cx="57" cy="48" rx="4.2" ry="3.2" fill="#ffffff" />
        <circle cx="41" cy="48" r="2.3" fill="#2f8b3f" />
        <circle cx="57" cy="48" r="2.3" fill="#2f8b3f" />
        <circle cx="41" cy="48" r="1" fill="#06120a" />
        <circle cx="57" cy="48" r="1" fill="#06120a" />
        <circle cx="41.7" cy="47.2" r="0.7" fill="#ffffff" />
        <circle cx="57.7" cy="47.2" r="0.7" fill="#ffffff" />
        <circle cx="41" cy="48" r="2.3" fill="none" stroke="#7ee65a" strokeWidth="0.6" />
        <circle cx="57" cy="48" r="2.3" fill="none" stroke="#7ee65a" strokeWidth="0.6" />
        <rect x="36.8" y="44.8" width="8.4" height="0" rx="1.4" fill="#f6c79e">
          <animate attributeName="height" values="0;0;6.2;0;0" keyTimes="0;0.92;0.95;0.98;1" dur="5s" repeatCount="indefinite" />
        </rect>
        <rect x="52.8" y="44.8" width="8.4" height="0" rx="1.4" fill="#f6c79e">
          <animate attributeName="height" values="0;0;6.2;0;0" keyTimes="0;0.92;0.95;0.98;1" dur="5s" repeatCount="indefinite" />
        </rect>
      </g>
      {/* Nose */}
      <path d="M49 50 Q46.8 56 49 59" stroke="#d99a6e" strokeWidth="1.1" strokeLinecap="round" fill="none" />
      {/* Smile */}
      <path d="M41 61 Q49 68 57 61" stroke="#7c2d12" strokeWidth="1.8" strokeLinecap="round" fill="none" />

      {/* The AI-picked cocktail (top-right) + sparkle */}
      <g>
        <ellipse cx="78" cy="13" rx="10" ry="1.8" fill="#cdebbc" />
        <path d="M68 13 L88 13 L78 26 Z" fill="#6fd94a" />
        <rect x="77" y="26" width="2" height="9" rx="1" fill="#cdebbc" />
        <rect x="71" y="34" width="14" height="2.4" rx="1.2" fill="#cdebbc" />
        {/* cherry garnish */}
        <circle cx="84" cy="11.5" r="2.1" fill="#e23a4e" />
        <path d="M84 9.6 Q87 7 89 9" stroke="#7a4a2a" strokeWidth="0.8" fill="none" />
        {/* sparkle — the "magic" pick */}
        <path d="M61 26 l1.3 3 3 1.3 -3 1.3 -1.3 3 -1.3 -3 -3 -1.3 3 -1.3 Z" fill="#eaffd9">
          <animate attributeName="opacity" values="0.25;1;0.25" dur="2s" repeatCount="indefinite" />
        </path>
      </g>
    </svg>
  );
}
